import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PhoneIncomingIcon, XIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { formatPhoneForDisplay } from '../lib/sendSms'

interface CallEvent {
  id: string
  call_id: string
  status: string | null
  caller_number: string | null
  caller_name: string | null
  line_number: string | null
  customer_id: string | null
  created_at: string
}

const AUTO_HIDE_MS = 45_000
const RECENT_WINDOW_MS = 30_000

/**
 * Screen-pop toast for incoming calls (Telebroad AccountRealTimeCalls webhook
 * writes ringing events to call_events; this listens via Supabase realtime).
 */
export function IncomingCallBanner() {
  const navigate = useNavigate()
  const [event, setEvent] = useState<CallEvent | null>(null)
  const [customerName, setCustomerName] = useState<string | null>(null)
  const seenCallIds = useRef<Set<string>>(new Set())
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showEvent = (row: CallEvent) => {
    if (!row?.call_id || seenCallIds.current.has(row.call_id)) return
    seenCallIds.current.add(row.call_id)
    setCustomerName(null)
    setEvent(row)
    if (hideTimer.current) clearTimeout(hideTimer.current)
    hideTimer.current = setTimeout(() => setEvent(null), AUTO_HIDE_MS)
  }

  useEffect(() => {
    const since = new Date(Date.now() - RECENT_WINDOW_MS).toISOString()
    supabase
      .from('call_events')
      .select('*')
      .eq('status', 'ringing')
      .gte('created_at', since)
      .order('created_at', { ascending: false })
      .limit(1)
      .then(({ data }) => {
        if (data && data[0]) showEvent(data[0] as CallEvent)
      })

    const channel = supabase
      .channel('call-events-pop')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'call_events' },
        (payload) => showEvent(payload.new as CallEvent)
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
      if (hideTimer.current) clearTimeout(hideTimer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!event?.customer_id) return
    supabase
      .from('customers')
      .select('company_name')
      .eq('id', event.customer_id)
      .single()
      .then(({ data }) => setCustomerName(data?.company_name || null))
  }, [event?.customer_id])

  if (!event) return null

  const callerLabel =
    event.caller_name || (event.caller_number ? formatPhoneForDisplay(event.caller_number) : 'Unknown caller')

  return (
    <div className="fixed top-4 right-4 z-[60] w-80 bg-white rounded-xl shadow-2xl border border-blue-200 overflow-hidden">
      <div className="flex items-start gap-3 p-4">
        <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
          <PhoneIncomingIcon className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Incoming call</p>
          <p className="text-sm font-semibold text-gray-900 truncate">{callerLabel}</p>
          {event.caller_number && event.caller_name && (
            <p className="text-xs text-gray-500">{formatPhoneForDisplay(event.caller_number)}</p>
          )}
          {customerName && <p className="text-xs text-gray-600 mt-1 truncate">{customerName}</p>}
          {event.customer_id ? (
            <button
              type="button"
              onClick={() => {
                setEvent(null)
                navigate(`/customers/view/${event.customer_id}`)
              }}
              className="mt-2 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              View Customer →
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                setEvent(null)
                navigate(`/customers/create?phone=${encodeURIComponent(event.caller_number || '')}`)
              }}
              className="mt-2 text-xs text-blue-600 hover:underline text-left"
            >
              Search / create customer
            </button>
          )}
          {event.line_number && (
            <p className="text-xs text-gray-400 mt-1">To {formatPhoneForDisplay(event.line_number)}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setEvent(null)}
          className="p-1 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
          aria-label="Dismiss"
        >
          <XIcon className="w-4 h-4 text-gray-500" />
        </button>
      </div>
    </div>
  )
}

export default IncomingCallBanner